// File: e1c/src/pages/ContentStudio.js

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
    Container, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, CircularProgress, TextField, LinearProgress, 
    FormControl, InputLabel, Select, MenuItem, Alert, Box, FormHelperText,
    Button, IconButton, Tooltip, Checkbox, FormControlLabel,
    Chip, InputAdornment, Grid, Stepper, Step, StepLabel, Autocomplete,
    Dialog, DialogTitle, DialogContent, DialogActions, Divider,
    List, ListItem, ListItemText, ListItemIcon as MuiListItemIcon, Tabs, Tab
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArchiveIcon from '@mui/icons-material/Archive';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import MicIcon from '@mui/icons-material/Mic';
import ArticleIcon from '@mui/icons-material/Article';
import PollIcon from '@mui/icons-material/Poll';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import LinkIcon from '@mui/icons-material/Link';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DownloadIcon from '@mui/icons-material/Download';

import { alpha, useTheme } from '@mui/material/styles';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import logger from '../utils/logger';
import VisualContentEditor from '../components/content_editor/VisualContentEditor';
import PatientQuestionRenderer from '../components/content_editor/PatientQuestionRenderer';
import ReadOnlySurveyRenderer from '../components/ReadOnlySurveyRenderer';
import useDebounce from '../hooks/useDebounce';
import authFetch from '../utils/authFetch';
import CareBundleBuilder from '../components/content_editor/CareBundleBuilder';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import ReactDOM from 'react-dom';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import UnifiedProgramBuilder from '../components/content_editor/UnifiedProgramBuilder'; 
import TrackChangesIcon from '@mui/icons-material/TrackChanges'; 
import ActivityTrackerEditor, { DEFAULT_TRACKER_CONFIG } from '../components/content_editor/ActivityTrackerEditor';

const getIconForContentType = (item) => {
        if (item.type === 'Program') {
            return <AccountTreeIcon sx={{ verticalAlign: 'middle', marginRight: '4px', fontSize: '1.25rem' }} />;
        }
        const type = item.content_type;
        const style = { verticalAlign: 'middle', marginRight: '4px', fontSize: '1.25rem' };
        try {
            if (type === 'education') {
                if (item.source_url && (item.source_url.includes('youtube.com/') || item.source_url.includes('vimeo.com/'))) {
                    return <VideoLibraryIcon sx={style} />;
                }
                if (item.original_file_ref || item.source_url) {
                    const name = (item.original_file_ref || item.source_url || '').toLowerCase();
                    if (name.endsWith('.mp4') || name.endsWith('.mov') || name.endsWith('.avi') || name.endsWith('.webm')) {
                        return <VideoLibraryIcon sx={style} />;
                    }
                    if (name.endsWith('.pdf')) {
                        return <PictureAsPdfIcon sx={style} />;
                    }
                }
                return <ArticleIcon sx={style} />;
            }
            if (type === 'questionnaire') return <PollIcon sx={style} />;
            if (type === 'form') return <PlaylistAddCheckIcon sx={style} />;
        } catch (e) {
        logger.warn("Error determining icon:", e, item);
        }
        return <DescriptionIcon sx={style} />;
    };

// --- Constants ---
const CONTENT_STUDIO_QUERY_KEY = 'contentStudioItems';
const SEED_QUESTIONS_QUERY_KEY = 'seedQuestions';
const CONTENT_STUDIO_DRAFT_KEY = 'contentStudioUnsavedDraft';
const STATUS_FILTER_OPTIONS = ['ALL', 'ACTIVE', 'DRAFT', 'PENDING_REVIEW', 'ARCHIVED', 'SUPERSEDED'];

const FE_TO_BE_CONTENT_TYPE_MAP = {
    'UPLOAD_EDUCATIONAL_RESOURCE': 'education',
    'BUILD_INTERACTIVE_QUESTIONNAIRE': 'questionnaire',
    'BUILD_QUESTION_BANK': 'form',
    'BUILD_SURVEY_FROM_MASTER': 'questionnaire',
    'BUILD_TRACKER': 'activity_log_request',
};

const SURVEY_CATEGORY_OPTIONS = [
    { value: "patient_reported_outcome", label: "Patient Reported Outcome (PRO)" },
    { value: "patient_satisfaction", label: "Patient Satisfaction" },
    { value: "intake_administrative", label: "Intake / Administrative" },
    { value: "health_wellness_assessment", label: "Health & Wellness Assessment" },
    { value: "clinical_checklist", label: "Clinical Checklist" },
    { value: "adherence_empathizer", label: "Adherence Reminder / Empathizer" },
    { value: "other_clinical", label: "Other Clinical Survey" },
];

const NEW_CONTENT_CATEGORIES = [
  {
    key: 'UPLOAD_EDUCATIONAL_RESOURCE',
    label: 'Upload a PDF or Video Link',
    description: 'Educational Resources - Ready to Share with Patients',
    type: 'EDUCATION',
    needsFileOrUrl: true,
    icon: <ArticleIcon />,
    defaultKeywords: ['RTM', 'PCM', 'CCM'],
    defaultTitlePrefix: ''
  },
  {
    key: 'BUILD_QUESTION_BANK',
    label: 'Design a Health Check-in',
    description: 'Adherence Trackers and Interactive Questionnaires (from master list)',
    type: 'FORM',
    icon: <PlaylistAddIcon />,
    usesSeed: 'question_bank_seeds',
    defaultKeywords: ['question bank', 'tracker', 'interactive questions', 'internal library'],
    defaultTitlePrefix: 'Health Check-in: ',
    needsEditor: false,
  },
  {
    key: 'BUILD_SURVEY_FROM_MASTER',
    label: 'Assemble a New Survey',
    description: 'Any - Custom, PROM, HCAHPS, CHNA, etc. (from master list)',
    type: 'QUESTIONNAIRE',
    icon: <FactCheckIcon />,
    usesSeed: 'survey_seeds',
    defaultKeywords: ['survey', 'feedback', 'patient input', 'assessment form'],
    defaultTitlePrefix: 'Patient Survey: ',
    needsEditor: false,
  },
  {
    key: 'BUILD_INTERACTIVE_QUESTIONNAIRE',
    label: 'Contribute a New Question',
    description: 'Seed Thoughtful Interactive Questions to the Master List',
    type: 'QUESTIONNAIRE',
    needsEditor: true,
    icon: <PollIcon />,
    defaultKeywords: ['seed question', 'custom authored', 'interactive q&a', 'assemble survey', 'questions bank'],
    defaultTitlePrefix: ''
  },
 {
    key: 'BUILD_TRACKER',
    label: 'Design a Custom Tracker',
    description: 'Create a reusable log for Activity, Bracing, CPAP, or Symptoms.',
    type: 'TRACKER',
    icon: <TrackChangesIcon />,
    defaultKeywords: ['tracker', 'log', 'daily check-in'],
    defaultTitlePrefix: 'Tracker: ',
    needsEditor: false, 
    isTracker: true
  },
];

const ALLOWED_MIME_TYPES = [
    "image/jpeg", "image/png", "image/heic", "image/heif", "image/gif", "image/webp",
    "video/mp4", "video/quicktime", "video/x-m4v", "video/webm",
    "audio/mpeg", "audio/ogg", "audio/wav", "audio/aac",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const ACCEPT_FILE_TYPES = ALLOWED_MIME_TYPES.join(",");
const CREATE_CONTENT_STEPS = ['Add Details & Content', 'Review & Finalize'];
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MIN_KEYWORDS_COUNT = 0;

// API Functions
const fetchContentItemsApi = async ({ queryKey }) => {
    const [_queryKey, tenantId, searchTerm] = queryKey;
    if (!tenantId) return [];
    logger.debug(`ContentStudio: Fetching content items for Tenant ${tenantId} with search: ${searchTerm}`);
    try {
        const searchParams = new URLSearchParams({ include_versions: 'true', limit: '400' });
        if (searchTerm) {
            searchParams.append('search', searchTerm);
        }
        const data = await authFetch(`content/?${searchParams.toString()}`);
        return Array.isArray(data) ? data : [];
    } catch (error) {
        logger.error("ContentStudio: Failed to fetch content items", error);
        throw error;
    }
};

const fetchSeedQuestionsApi = async ({ queryKey }) => {
    const [_queryKey, _seedTypeUnused, tenantId] = queryKey;
    if (!tenantId) return [];
    logger.debug(`ContentStudio: Fetching seed questions for tenant ${tenantId}.`);
    try {
        const data = await authFetch(`v1/survey-engine/seed-questions?active_only=true&limit=500`);
        return Array.isArray(data) ? data : [];
    } catch (error) {
        logger.error("ContentStudio: Failed to fetch seed questions", error);
        throw error;
    }
};

const createContentItemV1_API = async (payload) => {
    logger.debug("ContentStudio: Creating new content item (V1) with payload:", payload instanceof FormData ? "FormData (see details in subsequent logs if applicable)" : payload);
    let options;
    if (payload instanceof FormData) {
        options = { method: 'POST', body: payload };
    } else {
        options = { method: 'POST', body: JSON.stringify(payload), headers: { 'Content-Type': 'application/json' } };
    }
    const data = await authFetch('content/', options);
    return data;
};

const updateVersionMetadata_API = async ({ itemId, metadata }) => {
    logger.debug(`ContentStudio: Updating metadata for item version ID ${itemId} with:`, metadata);
    const data = await authFetch(`content/${itemId}/metadata`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(metadata),
    });
    return data;
};


const archiveLatestVersion_API = async ({ contentItemId }) => {
    logger.debug(`ContentStudio: Archiving latest version for item series ID ${contentItemId}`);
    await authFetch(`content/${contentItemId}/versions/latest/archive`, {
        method: 'PATCH',
    });
    return { success: true, message: "Item's latest version archived request sent." };
};

const deleteContentSeries_API = async ({ contentItemId }) => {
    logger.debug(`ContentStudio: PERMANENTLY DELETING content series ID ${contentItemId}`);
    await authFetch(`content/${contentItemId}`, { method: 'DELETE' });
    return { success: true, message: "Item series permanently deleted." };
};

const deleteProtocol_API = async ({ protocolId }) => {
    logger.debug(`ContentStudio: Deleting protocol (bundle) series ID ${protocolId}`);
    await authFetch(`protocols/${protocolId}`, { method: 'DELETE' }); 
    return { success: true, message: "Protocol/Bundle deleted." };
};

const deleteProgram_API = async ({ programId }) => {
    logger.debug(`ContentStudio: Deleting program series ID ${programId}`);
    await authFetch(`programs/${programId}`, { method: 'DELETE' }); 
    return { success: true, message: "Program deleted." };
};

const createOrUpdateTemplateApi = async (templatePayload) => {
    logger.debug("ContentStudio: Saving Monitoring Template (Protocol)...", templatePayload);
    const data = await authFetch(`/api/content/v1/therapy-modules/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(templatePayload),
    });
    return data;
};

const validateClientSideInteractiveContent = (content, contentTypeForValidation, currentCategoryKey) => {
    logger.debug(`Client Validating Interactive Content (Type: ${contentTypeForValidation}, Category: ${currentCategoryKey}):`, content);

    if (!Array.isArray(content)) {
        throw new Error(`Content for ${contentTypeForValidation} must be an array of blocks/questions.`);
    }
    if (currentCategoryKey === 'BUILD_INTERACTIVE_QUESTIONNAIRE' && content.length === 0) {
        throw new Error(`Content cannot be empty. Please design your question.`);
    }
    for (const [index, block] of content.entries()) {
        if (!block || typeof block !== 'object') throw new Error(`Block at index ${index} invalid.`);
        if (!block.id?.trim()) throw new Error(`Block at index ${index} needs ID.`);

        const blockTypeToCheck = block.block_type || block.type;
        if (!blockTypeToCheck?.trim()) throw new Error(`Block ID ${block.id} needs type.`);

        const blockTypeUpper = String(blockTypeToCheck).toUpperCase();
        const questionText = block.prompt_text || block.question;

        if (!questionText?.trim() && !["INFO_TEXT", "IMAGE", "VIDEO", "DIVIDER", "PAIN_MAP"].includes(blockTypeUpper)) {
             throw new Error(`Question text or prompt required for block type '${blockTypeToCheck}'. ID: ${block.id}`);
        }
        if (typeof block.options !== 'object' || block.options === null) {
             if (!["INFO_TEXT", "IMAGE", "VIDEO", "DIVIDER"].includes(blockTypeUpper)) {
                throw new Error(`Block ID: ${block.id}, Type: ${blockTypeToCheck} 'options' must be an object.`);
             }
        }
        if (blockTypeUpper === "PAIN_MAP") {
            if (block.options && !block.options.imageUrl) {
                throw new Error(`Pain Map block ID ${block.id} is missing imageUrl in options.`);
            }
            if (block.options && !Array.isArray(block.options.points)) {
                throw new Error(`Pain Map block ID ${block.id} is missing 'points' array in options.`);
            }
        }
        if (blockTypeUpper === "SINGLE_CHOICE_SELECT" || blockTypeUpper === "MULTI_CHOICE_SELECT") {
            if (!block.options || !Array.isArray(block.options.choices) || block.options.choices.length === 0) {
                throw new Error(`Choices required for '${blockTypeToCheck}'. ID: ${block.id}`);
            }
            block.options.choices.forEach(choice => {
                if (!choice.value?.trim()) throw new Error(`Choice needs non-empty 'value'. ID: ${block.id}`);
                if (!choice.label?.trim()) throw new Error(`Choice needs non-empty 'label'. ID: ${block.id}`);
            });
        }
    }
    logger.debug("Client-side interactive content validation passed.");
};

// --- START: Video Helper Functions ---
const getEmbedUrl = (url) => {
    if (!url) return null;
    try {
        const urlObj = new URL(url);
        if (urlObj.hostname.includes('youtube.com') || urlObj.hostname.includes('youtu.be')) {
            const videoId = urlObj.hostname.includes('youtu.be')
                ? urlObj.pathname.substring(1)
                : urlObj.searchParams.get('v');
            if (videoId) return `https://www.youtube.com/embed/${videoId}`;
        }
        if (urlObj.hostname.includes('vimeo.com')) {
            const videoId = urlObj.pathname.substring(1);
            if (videoId && /^[0-9]+$/.test(videoId)) return `https://player.vimeo.com/video/${videoId}`;
        }
    } catch (e) { console.error("Error parsing video URL:", e); }
    return null;
};

const isNativeVideoFile = (url) => {
    if (!url) return false;
    const lowerUrl = url.toLowerCase();
    return lowerUrl.endsWith('.mp4') || lowerUrl.endsWith('.webm') || lowerUrl.endsWith('.mov');
};
// --- END: Video Helper Functions ---

function ContentStudio() {
    const { user, selectedTenant, isLoading: isAuthContextLoading } = useAuth();
    const queryClient = useQueryClient();
    const theme = useTheme();
    const tenantId = selectedTenant?.tenant_id;
    const canManageContent = useMemo(() =>
        user?.role === 'tenant_admin' || user?.role === 'physician_qhp' || user?.is_content_editor,
        [user]
    );

    const [showAllVersions, setShowAllVersions] = useState(false);
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [activeTab, setActiveTab] = useState('ALL');
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    const [modalMode, setModalMode] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [formData, setFormData] = useState({});
    const [formError, setFormError] = useState('');
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [selectedItemForView, setSelectedItemForView] = useState(null);
   

    const initialNewContentData = useMemo(() => ({
        categoryKey: '', title: '', keywords: [], shortDescription: '',
        survey_category: '',
        scoring_rules_definition: '',
        processed_content: [], item_metadata: {}, source_url: '', status: 'DRAFT',
        selectedSeedIds: [],
    }), []);

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [createModalStep, setCreateModalStep] = useState(0);
    const [newContentData, setNewContentData] = useState(() => {
        try {
            const savedDraft = localStorage.getItem(CONTENT_STUDIO_DRAFT_KEY);
            if (savedDraft) {
                const parsedDraft = JSON.parse(savedDraft);
                // Basic validation
                if (parsedDraft && (parsedDraft.categoryKey || parsedDraft.processed_content?.length > 0)) {
                    logger.info("ContentStudio: Loaded unsaved draft from localStorage.");
                    return parsedDraft;
                }
            }
        } catch (e) {
            logger.error("ContentStudio: Failed to parse saved draft.", e);
            localStorage.removeItem(CONTENT_STUDIO_DRAFT_KEY);
        }
        return initialNewContentData; // Fallback to initial data
    });
    
    
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileError, setFileError] = useState('');
    const [createFormErrors, setCreateFormErrors] = useState({});
    const [isListening, setIsListening] = useState(false);
    const fileInputRef = useRef(null);
    const recognitionRef = useRef(null);
    const [sttError, setSttError] = useState('');
    const [sttSupported, setSttSupported] = useState(true);
    const [isAiCreating, setIsAiCreating] = useState(false);
    const [isTemplateEditorOpen, setIsTemplateEditorOpen] = useState(false);
    const [duplicateBundleData, setDuplicateBundleData] = useState(null);
    const [isBundleEditMode, setIsBundleEditMode] = useState(false);
    const [isProgramBuilderOpen, setIsProgramBuilderOpen] = useState(false);
    const [programToEdit, setProgramToEdit] = useState(null);
    // Add this line
    const [contentTypeFilter, setContentTypeFilter] = useState(''); // State for the new filter

    const TABS = [
        { value: 'ALL', label: 'All Assets' },
        { value: 'PROGRAMS', label: 'Programs', type: 'Program' },
        { value: 'BUNDLES', label: 'Care Bundles', type: ['Care Bundle', 'Med Protocol'] },
        { value: 'CONTENT_ITEMS', label: 'Content Items', type: 'Content Item' },
    ];
    useEffect(() => {
        // Auto-save any changes to the new content form to localStorage
        try {
            // Compare to initial data to avoid saving a blank form after a submit/reset
            if (JSON.stringify(newContentData) !== JSON.stringify(initialNewContentData)) {
                // FIXED: Use dynamic key based on category to prevent collisions
                const dynamicKey = `${CONTENT_STUDIO_DRAFT_KEY}_${newContentData.categoryKey}`;
                localStorage.setItem(dynamicKey, JSON.stringify(newContentData));
            } 
            // Note: We don't auto-clear here anymore because switching categories would clear valid drafts.
            // Drafts should be cleared explicitly on successful submit or cancel.
        } catch (e) {
            logger.warn("ContentStudio: Could not auto-save draft to localStorage.", e);
        }
    }, [newContentData, initialNewContentData]);

    const CONTENT_TYPE_FILTER_OPTIONS = [
        { value: '', label: 'All Content Types' },
        { value: 'questionnaire', label: 'Questionnaire' },
        { value: 'form', label: 'Form' },
        { value: 'education', label: 'Education' },
        { value: 'video', label: 'Video (Direct)' }, // If you have a direct 'video' type
        { value: 'pdf', label: 'PDF (Direct)' },     // If you have a direct 'pdf' type
        // Add other relevant content types used in your system
    ];

    const { data: allContentItems = [], isLoading: isLoadingContent, error: queryError } = useQuery({
        queryKey: [CONTENT_STUDIO_QUERY_KEY, tenantId, debouncedSearchTerm],
        queryFn: fetchContentItemsApi,
        enabled: !!tenantId && !isAuthContextLoading,
        staleTime: 1000 * 60 * 2,
    });
    const navigate = useNavigate();
    const { data: allPrograms = [], isLoading: isLoadingPrograms } = useQuery({
        queryKey: ['allPrograms', tenantId],
        queryFn: () => authFetch('/api/programs'),
        enabled: !!tenantId && !isAuthContextLoading,
    }); 
    const { data: allProtocols = [], isLoading: isLoadingProtocols } = useQuery({
        queryKey: ['allProtocols', tenantId],
        queryFn: () => authFetch('/api/protocols'),
        enabled: !!tenantId && !isAuthContextLoading,
    });

    const currentCategoryDetails = useMemo(() =>
        NEW_CONTENT_CATEGORIES.find(c => c.key === newContentData.categoryKey),
    [newContentData.categoryKey]);

    const { data: seedQuestions = [], isLoading: isLoadingSeedQuestions } = useQuery({
        queryKey: [SEED_QUESTIONS_QUERY_KEY, currentCategoryDetails?.usesSeed, tenantId],
        queryFn: fetchSeedQuestionsApi,
        enabled: !!tenantId && !!currentCategoryDetails?.usesSeed && isCreateModalOpen && createModalStep === CREATE_CONTENT_STEPS.indexOf('Add Details & Content') && !isAuthContextLoading,
        staleTime: 1000 * 60 * 10,
    });

    const ASSET_TYPE_SORT_ORDER = {
        'Program': 0, 
        'Care Bundle': 1,
        'Med Protocol': 2,
        'Content Item': 3,
    };

    const filteredContentItems = useMemo(() => {
        const programs = (Array.isArray(allPrograms) ? allPrograms : []).map(program => ({
            ...program,
            id: program.id,
            title: program.name,
            type: 'Program', 
            typeEnum: program.journey_type,
            status: program.is_active ? 'active' : 'inactive',
            version: 'N/A', 
            updated_at: program.updated_at,
            keywords: [], 
            is_latest_version: true, 
        }));

        const content = (Array.isArray(allContentItems) ? allContentItems : []).map(item => ({
            ...item,
            id: item.id,
            title: item.title,
            type: 'Content Item', 
            typeEnum: item.content_type,
            status: item.status,
            version: item.version,
            updated_at: item.updated_at,
            keywords: item.keywords || [],
            is_latest_version: item.is_latest_version,
        }));

        const bundles = (Array.isArray(allProtocols) ? allProtocols : []).map(bundle => ({
            ...bundle,
            id: bundle.id,
            title: bundle.name,
            type: bundle.protocol_type === 'care_bundle' ? 'Care Bundle' : 'Med Protocol', 
            typeEnum: bundle.protocol_type,
            status: bundle.is_active ? 'active' : 'inactive',
            version: bundle.version || 1, 
            updated_at: bundle.updated_at,
            keywords: bundle.module_definition?.keywords || [], 
            is_latest_version: bundle.is_latest_version !== false, 
        }));

        let combinedItems = [...programs, ...content, ...bundles]; 

        if (!showAllVersions) {
            combinedItems = combinedItems.filter(item => item.is_latest_version);
        }
        if (statusFilter !== 'ALL') {
            combinedItems = combinedItems.filter(item => item.status?.toUpperCase() === statusFilter);
        }

        if (debouncedSearchTerm) {
            const lowerSearch = debouncedSearchTerm.toLowerCase();
            combinedItems = combinedItems.filter(item => 
                (item.title || '').toLowerCase().includes(lowerSearch) ||
                (item.keywords || []).some(k => k.toLowerCase().includes(lowerSearch))
            );
        }

        combinedItems.sort((a, b) => {
            const aPriority = ASSET_TYPE_SORT_ORDER[a.type] || 99;
            const bPriority = ASSET_TYPE_SORT_ORDER[b.type] || 99;
    
            if (aPriority !== bPriority) {
                return aPriority - bPriority;
            }
    
            const titleA = a.title || '';
            const titleB = b.title || '';
            const titleCompare = titleA.localeCompare(titleB);
            if (titleCompare !== 0) return titleCompare;
    
            return (b.version || 0) - (a.version || 0);
        });

        return combinedItems;
    }, [allContentItems, allProtocols, allPrograms, showAllVersions, statusFilter, debouncedSearchTerm]);

    const itemsForCurrentTab = useMemo(() => {
        let items = [];
        if (activeTab === 'ALL') {
            items = filteredContentItems;
        } else {
            const tabConfig = TABS.find(t => t.value === activeTab);
            if (tabConfig) {
                const typesToFilter = Array.isArray(tabConfig.type) ? tabConfig.type : [tabConfig.type];
                items = filteredContentItems.filter(item => typesToFilter.includes(item.type));
            }
        }

        // Apply Content Type filter only on the Content Items tab
        if (activeTab === 'CONTENT_ITEMS' && contentTypeFilter) {
            // Handle 'education' potentially including videos/pdfs based on your getIcon logic
            if (contentTypeFilter === 'education') {
                items = items.filter(item =>
                    item.content_type === 'education' ||
                    item.content_type === 'pdf' || // Include direct pdf type if needed
                    (item.content_type === 'video') || // Include direct video type if needed
                    (item.source_url && (item.source_url.includes('youtube.com') || item.source_url.includes('vimeo.com'))) ||
                    (item.original_file_ref && (item.original_file_ref.toLowerCase().endsWith('.pdf')))
                );
            } else {
                items = items.filter(item => item.content_type === contentTypeFilter);
            }
        }

        return items;

    }, [filteredContentItems, activeTab, contentTypeFilter]); // Add contentTypeFilter dependency

// Place this near your other state variables (e.g., after contentTypeFilter)
    const [isBulkUploading, setIsBulkUploading] = useState(false);
    const [bulkProgress, setBulkProgress] = useState(0);

    const handleBulkFileChange = async (event) => {
        const files = Array.from(event.target.files);
        if (!files.length) return;

        if (!window.confirm(`Ready to import ${files.length} files? This will create a new active content item for each file.`)) {
            event.target.value = null; 
            return;
        }

        setIsBulkUploading(true);
        setBulkProgress(0);
        let completedCount = 0;
        let errorCount = 0;

        for (const file of files) {
            try {
                if (file.size > MAX_FILE_SIZE_BYTES) {
                    logger.error(`Skipping ${file.name}: exceeds size limit.`);
                    errorCount++;
                    continue;
                }

                const title = file.name.split('.').slice(0, -1).join('.') || file.name;
                const metadata = {
                    title: title,
                    content_type: 'education',
                    keywords: ['bulk_import'],
                    status: 'active',
                    description: `Bulk imported on ${new Date().toLocaleDateString()}`
                };

                const formData = new FormData();
                formData.append('metadata', JSON.stringify(metadata));
                formData.append('file', file);

                await createContentItemV1_API(formData);

            } catch (err) {
                logger.error(`Failed to upload ${file.name}`, err);
                errorCount++;
            }

            completedCount++;
            setBulkProgress((completedCount / files.length) * 100);
        }

        setIsBulkUploading(false);
        event.target.value = null;
        queryClient.invalidateQueries({ queryKey: [CONTENT_STUDIO_QUERY_KEY, tenantId] });
        
        const successCount = files.length - errorCount;
        alert(`Bulk Import Complete.\nSuccess: ${successCount}\nFailed: ${errorCount}`);
    };

    const isDuplicateTitle = useMemo(() => {
        // Safety: Ensure current title is a string before checking length
        const currentTitle = newContentData.title || '';
        if (currentTitle.trim().length < 3) return false;
        
        // FIX: Handle null/undefined titles safely
        const normalize = t => String(t || '').trim().toLowerCase();
        const targetTitle = normalize(currentTitle);
        
        // Check against loaded items (ignore archived ones)
        return allContentItems.some(item => 
            normalize(item.title) === targetTitle && 
            item.status !== 'archived'
        );
    }, [newContentData.title, allContentItems]);

    const handleModalClose = useCallback(() => {
        setModalMode(null); setSelectedItem(null); setFormData({}); setFormError('');
    }, []);

    const handleViewModalClose = useCallback(() => {
        setIsViewModalOpen(false);
        setSelectedItemForView(null);
    }, []);


    const handleCreateModalClose = useCallback(() => {
        const isDirty = JSON.stringify(newContentData) !== JSON.stringify(initialNewContentData);

        if (isDirty) {
            if (!window.confirm("You have unsaved changes. Are you sure you want to discard this draft?")) {
                return; // Don't close the modal
            }
            // User confirmed discard, so clear the saved draft
            localStorage.removeItem(CONTENT_STUDIO_DRAFT_KEY);
        }

        // Proceed with closing and resetting
        setIsCreateModalOpen(false);
        setTimeout(() => {
            setCreateModalStep(0);
            setNewContentData(initialNewContentData); // Reset state
            setSelectedFile(null);
            setFileError('');
            setCreateFormErrors({});
        }, 300);
    }, [initialNewContentData, newContentData]); // <-- Add newContentData dependency



    const handleCreateModalBack = useCallback(() => {
        setCreateFormErrors({});
        setCreateModalStep(prev => Math.max(0, prev - 1));
    }, []);


    const handleNewContentInputChange = useCallback((event) => {
        const { name, value, type, checked } = event.target;
        if (name === 'source_url' && value.trim() !== '') {
            setSelectedFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
        setNewContentData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        if (createFormErrors[name]) {
            setCreateFormErrors(prevErrs => ({ ...prevErrs, [name]: null }));
        }
    }, [createFormErrors]);

    const handleNewContentMultiSelectChange = useCallback((name, newValue) => {
        const arrayValue = Array.isArray(newValue) ?
            newValue.map(v => (typeof v === 'string' ? v.trim() : (v.inputValue || v.label || String(v)))).filter(Boolean) : [];
        setNewContentData(prev => ({ ...prev, [name]: arrayValue }));
        if (createFormErrors[name] && name === "keywords" && arrayValue.length >= MIN_KEYWORDS_COUNT) {
            setCreateFormErrors(prevErrs => ({ ...prevErrs, keywords: null }));
        } else if (createFormErrors[name]) {
             setCreateFormErrors(prevErrs => ({ ...prevErrs, [name]: null }));
        }
    }, [createFormErrors]);

    const handleFileChange = useCallback((event) => {
        const file = event.target.files[0] || null;
        setFileError('');
        if (createFormErrors.fileOrUrl) setCreateFormErrors(prev => ({...prev, fileOrUrl: null}));
        if (createFormErrors.file) setCreateFormErrors(prev => ({...prev, file: null}));
        if (file) {
            if (!ALLOWED_MIME_TYPES.includes(file.type)) {
                setFileError(`Unsupported file type: ${file.type}. Allowed: ${ACCEPT_FILE_TYPES.split(',').map(s=>s.split('/')[1]).join(', ')}`);
                setSelectedFile(null); event.target.value = null; return;
            }
            if (file.size > MAX_FILE_SIZE_BYTES) {
                setFileError(`File too large. Max ${MAX_FILE_SIZE_MB}MB.`);
                setSelectedFile(null); event.target.value = null; return;
            }
            setSelectedFile(file);
            setNewContentData(prev => {
                let newTitle = prev.title;
                if (currentCategoryDetails?.key === 'UPLOAD_EDUCATIONAL_RESOURCE' &&
                    (!prev.title || prev.title === currentCategoryDetails?.defaultTitlePrefix) && file.name) {
                     newTitle = file.name.split('.').slice(0, -1).join('.') || file.name;
                }
                    return { ...prev, title: newTitle, source_url: '' };
            });
        } else { setSelectedFile(null); }
    }, [currentCategoryDetails, createFormErrors]);

    const handleNewVisualContentChange = useCallback((updater) => {
        setNewContentData(prevData => {
            const currentContent = prevData.processed_content || [];
            const newProcessedContent = updater(currentContent);
            return { ...prevData, processed_content: newProcessedContent };
        });
    }, []);

    const handleSeedQuestionToggle = useCallback((seedId) => {
        setNewContentData(prev => {
            const currentSelectedIds = prev.selectedSeedIds || [];
            const newSelectedIds = currentSelectedIds.includes(seedId)
                ? currentSelectedIds.filter(id => id !== seedId)
                : [...currentSelectedIds, seedId];
            return { ...prev, selectedSeedIds: newSelectedIds };
        });
        if (createFormErrors.seeds && newContentData.selectedSeedIds && newContentData.selectedSeedIds.length > 0) {
             setCreateFormErrors(prev => ({...prev, seeds: null}));
        }
    }, [createFormErrors, newContentData.selectedSeedIds]);

    useEffect(() => {
        if (createFormErrors.processed_content &&
            Array.isArray(newContentData.processed_content) &&
            newContentData.processed_content.length > 0) {
            setCreateFormErrors(prevErrs => {
                if (prevErrs.processed_content) {
                    const { processed_content, ...rest } = prevErrs;
                    return rest;
                }
                return prevErrs;
            });
        }
    }, [newContentData.processed_content, createFormErrors.processed_content]);


    useEffect(() => {
        if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
            const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
            recognitionRef.current = new SpeechRecognitionAPI();
            recognitionRef.current.continuous = false;
            recognitionRef.current.interimResults = false;

            recognitionRef.current.onstart = () => {
                logger.debug('STT: Recognition started.');
                setIsListening(true);
                setSttError('');
            };

            recognitionRef.current.onresult = (event) => {
                const transcript = event.results[event.results.length - 1][0].transcript.trim();
                logger.info(`STT: Transcript received: "${transcript}"`);
                setSearchTerm(transcript);
            };

            recognitionRef.current.onerror = (event) => {
                logger.error(`STT: Error - ${event.error}`);
                let friendlyMessage = `Speech recognition error: ${event.error}.`;
                if (event.error === 'no-speech') {
                    friendlyMessage = 'No speech was detected. Please try again.';
                } else if (event.error === 'audio-capture') {
                    friendlyMessage = 'Microphone access problem. Please check your microphone settings.';
                } else if (event.error === 'not-allowed') {
                    friendlyMessage = 'Microphone permission denied. Please allow access in your browser settings and try again.';
                }
                setSttError(friendlyMessage);
            };

            recognitionRef.current.onend = () => {
                logger.debug('STT: Recognition ended.');
                setIsListening(false);
            };

            setSttSupported(true);
        } else {
            logger.warn("STT: Speech recognition not supported in this browser.");
            setSttSupported(false);
        }

        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.abort();
                recognitionRef.current.onstart = null;
                recognitionRef.current.onresult = null;
                recognitionRef.current.onerror = null;
                recognitionRef.current.onend = null;
            }
        };
    }, []);

    const mutationOptions = useMemo(() => ({
        onSuccess: (data, variables) => {
            logger.info("API Mutation successful.", { data, variables });
            localStorage.removeItem(CONTENT_STUDIO_DRAFT_KEY); // <-- CLEARS DRAFT
            
            queryClient.invalidateQueries({ queryKey: [CONTENT_STUDIO_QUERY_KEY, tenantId, debouncedSearchTerm] });
            queryClient.invalidateQueries({ queryKey: ['allProtocols', tenantId] });
            queryClient.invalidateQueries({ queryKey: ['allPrograms', tenantId] });
            
            // Manually close and reset *all* modals/forms
            setModalMode(null);
            setSelectedItem(null);
            setFormData({});
            setFormError('');
            setIsCreateModalOpen(false);

            setTimeout(() => {
                setCreateModalStep(0);
                setNewContentData(initialNewContentData); // Reset state
                setSelectedFile(null);
                setFileError('');
                setCreateFormErrors({});
            }, 300);
        },   

        onError: (err, variables) => {
            logger.error("API Mutation error:", { err, variables });
            const responseDetail = err?.response?.data?.detail || err?.data?.detail;
            let detailMessage = 'An unexpected error occurred.';
            if (typeof responseDetail === 'string') detailMessage = responseDetail;
            else if (Array.isArray(responseDetail)) detailMessage = responseDetail.map(e => `${e.loc ? e.loc.join(' -> ') : 'Error'}: ${e.msg}`).join('; ');
            else if (typeof responseDetail === 'object' && responseDetail !== null) detailMessage = JSON.stringify(responseDetail);
            else if (err?.message) detailMessage = err.message;

            if (variables.payload instanceof FormData) {
                logger.error("API Mutation error with FormData. Detail:", detailMessage);
            } else {
                logger.error("API Mutation error with JSON. Detail:", detailMessage, "Variables:", variables);
            }

            if (modalMode) setFormError(detailMessage);
            if (isCreateModalOpen) setCreateFormErrors(prev => ({ ...prev, general: detailMessage }));
        },
    }), 
    [queryClient, tenantId, debouncedSearchTerm, modalMode, isCreateModalOpen, initialNewContentData]
    );    

    const addMutation = useMutation({ mutationFn: createContentItemV1_API, ...mutationOptions });
    const editMetaMutation = useMutation({ mutationFn: updateVersionMetadata_API, ...mutationOptions });
    const archiveMutation = useMutation({ mutationFn: archiveLatestVersion_API, ...mutationOptions });

    const deleteMutation = useMutation({ mutationFn: deleteContentSeries_API, ...mutationOptions });
    const deleteProtocolMutation = useMutation({ mutationFn: deleteProtocol_API, ...mutationOptions });
    const deleteProgramMutation = useMutation({ mutationFn: deleteProgram_API, ...mutationOptions });
    const saveTemplateMutation = useMutation({
        mutationFn: createOrUpdateTemplateApi,
        ...mutationOptions
    });

    const isMutating = useCallback(() => addMutation.isPending || editMetaMutation.isPending || archiveMutation.isPending || deleteMutation.isPending || saveTemplateMutation.isPending || deleteProtocolMutation.isPending, [addMutation.isPending, editMetaMutation.isPending, archiveMutation.isPending, deleteMutation.isPending, saveTemplateMutation.isPending, deleteProtocolMutation.isPending]);

    const handleCreateWithAi = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        event.target.value = null;

        setIsAiCreating(true);
        setCreateFormErrors({});
        const formData = new FormData();
        formData.append('file', file);

        try {
            const aiGeneratedData = await authFetch('assistant/parse-questionnaire-pdf', {
                method: 'POST',
                body: formData,
            });

            // --- START EDIT: Sanitize blocks to pass validation ---
            const sanitizedBlocks = (aiGeneratedData.processed_content || []).map((block, idx) => ({
                ...block,
                id: block.id || `ai_block_${Date.now()}_${idx}`, // Injects mandatory ID
                options: block.options || {},                  // Injects mandatory options
                type: (block.type || 'open_text').toLowerCase() // Matches backend Enums
            }));
            // --- END EDIT ---

            let scoringDefString = "";
            if (aiGeneratedData.scoring_rules_definition) {
                scoringDefString = JSON.stringify(aiGeneratedData.scoring_rules_definition, null, 2);
            }

            setNewContentData({
                ...initialNewContentData,
                ...aiGeneratedData,
                processed_content: sanitizedBlocks, // Use sanitized blocks
                categoryKey: 'BUILD_INTERACTIVE_QUESTIONNAIRE',
                scoring_rules_definition: scoringDefString,
            });
            setIsCreateModalOpen(true);

        } catch (error) {
            logger.error("ContentStudio: AI creation failed", error);
            setCreateFormErrors({ general: `Failed to process PDF with AI: ${error.message}` });
            setIsCreateModalOpen(true);
        } finally {
            setIsAiCreating(false);
        }
    };

    const handleOpenCreateTrackerModal = useCallback(() => {
        const isDirty = JSON.stringify(newContentData) !== JSON.stringify(initialNewContentData);
        const newCategoryKey = 'BUILD_TRACKER';
        if (isDirty && newContentData.categoryKey !== newCategoryKey) {
            if (!window.confirm("Discard unsaved draft?")) return;
        }
        
        const stableNamespace = `trk_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

        // --- FIX: Initialize with Config Template instead of Blocks ---
        const initialTrackerConfig = {
            ...DEFAULT_TRACKER_CONFIG,
            tracker_namespace: stableNamespace
        };

        setNewContentData({
            ...initialNewContentData,
            categoryKey: 'BUILD_TRACKER',
            title: '',
            keywords: ['tracker', 'activity log'],
            shortDescription: '',
            processed_content: [], // CRITICAL: Empty blocks ensures Patient App uses Config Mode
            item_metadata: initialTrackerConfig 
        });

        setSelectedFile(null);
        setFileError('');
        setCreateFormErrors({});
        setCreateModalStep(0);
        setIsCreateModalOpen(true);
    }, [initialNewContentData, newContentData]);

    const handleOpenCreateQuestionnaireModal = useCallback(() => {
        const isDirty = JSON.stringify(newContentData) !== JSON.stringify(initialNewContentData);
        const newCategoryKey = 'BUILD_INTERACTIVE_QUESTIONNAIRE';

        if (isDirty && newContentData.categoryKey !== newCategoryKey) {
            if (!window.confirm("You have an unsaved draft for a different content type. Do you want to discard it and start a new 'Build Materials'?")) {
                return; 
            }
        }

        if (!isDirty || newContentData.categoryKey !== newCategoryKey) {
            const questionnaireCategory = NEW_CONTENT_CATEGORIES.find(c => c.key === newCategoryKey);
            setNewContentData({
                ...initialNewContentData,
                categoryKey: newCategoryKey,
                title: questionnaireCategory?.defaultTitlePrefix || '',
                keywords: questionnaireCategory?.defaultKeywords ? [...questionnaireCategory.defaultKeywords] : [],
                shortDescription: '',
            });
        }
        
        setSelectedFile(null);
        setFileError('');
        setCreateFormErrors({});
        setCreateModalStep(0);
        setIsCreateModalOpen(true);
    }, [initialNewContentData, newContentData]);

    const handleOpenUploadResourceModal = useCallback(() => {
        const isDirty = JSON.stringify(newContentData) !== JSON.stringify(initialNewContentData);
        const newCategoryKey = 'UPLOAD_EDUCATIONAL_RESOURCE';

        if (isDirty && newContentData.categoryKey !== newCategoryKey) {
            if (!window.confirm("You have an unsaved draft for a different content type. Do you want to discard it and start a new 'Upload Resource'?")) {
                return; 
            }
        }
        if (!isDirty || newContentData.categoryKey !== newCategoryKey) {
            const uploadCategory = NEW_CONTENT_CATEGORIES.find(c => c.key === newCategoryKey);
            setNewContentData({
                ...initialNewContentData,
                categoryKey: newCategoryKey,
                title: uploadCategory?.defaultTitlePrefix || '',
                keywords: uploadCategory?.defaultKeywords ? [...uploadCategory.defaultKeywords] : [],
                shortDescription: '',
            });
        }

        setSelectedFile(null);
        setFileError('');
        setCreateFormErrors({});
        setCreateModalStep(0);
        setIsCreateModalOpen(true);
    }, [initialNewContentData, newContentData]);


    const handleOpenViewModal = useCallback((item) => {
        setSelectedItemForView(item);
        setIsViewModalOpen(true);
    }, []);

    const handleOpenEditMetaModal = useCallback(async (item) => {
        setSelectedItem(item);
        
        // [FIX] Robust flattener that handles legacy nested AND new flat data
        const flattenMetadata = (meta) => {
            if (!meta) return JSON.parse(JSON.stringify(DEFAULT_TRACKER_CONFIG));
            
            // Case A: Legacy Nested (tracker_config)
            if (meta.tracker_config) {
                return {
                    ...meta.tracker_config,
                    tracker_namespace: meta.tracker_namespace || meta.tracker_config.tracker_namespace
                };
            }
            // Case B: Already Flat (Ideal)
            return meta;
        };

        let currentItemMetadata = flattenMetadata(item.item_metadata);
        
        // [FIX] Ensure we have the critical keys. If they are missing (even if other keys exist), fill them in.
        if (item.content_type === 'activity_log_request') {
             if (!currentItemMetadata.labels) currentItemMetadata.labels = DEFAULT_TRACKER_CONFIG.labels;
             if (!currentItemMetadata.units) currentItemMetadata.units = DEFAULT_TRACKER_CONFIG.units;
             if (!currentItemMetadata.goals) currentItemMetadata.goals = DEFAULT_TRACKER_CONFIG.goals;
        }

        // Initialize form
        setFormData({
            id: item.id, 
            title: item.title || '',
            keywords: (item.keywords || []).join(', '),
            intended_purpose_tags: item.intended_purpose_tags || [],
            shortDescription: item.short_description || item.sub_specialty || '',
            status: item.status?.toUpperCase() || 'DRAFT',
            content_type: item.content_type, 
            version: item.version,
            source_url: item.source_url || '',
            item_metadata: currentItemMetadata, // <-- Now guaranteed to be flat and complete
            survey_category: item.survey_category || '',
            scoring_rules_definition: item.scoring_rules_definition ? JSON.stringify(item.scoring_rules_definition, null, 2) : '',
        });
        
        setFormError(''); 
        setModalMode('editMeta');
        try {
            const fullItem = await authFetch(`content/${item.id}`);
            
            if (fullItem && fullItem.item_metadata) {
                console.log("DEBUG: Refreshed metadata from server:", fullItem.item_metadata);
                let refreshedMetadata = fullItem.item_metadata;
                
                if (refreshedMetadata.tracker_config) {
                    refreshedMetadata = {
                        ...refreshedMetadata.tracker_config, 
                        tracker_namespace: refreshedMetadata.tracker_namespace 
                    };
                }

                setFormData(prev => ({
                    ...prev,
                    item_metadata: refreshedMetadata
                }));
            }
        } catch (error) {
            console.warn("DEBUG: Could not fetch full item details.", error);
        }
    }, []);
    const handleOpenEditBundleModal = useCallback((item) => {
        setDuplicateBundleData(item);
        setIsBundleEditMode(true);
        setIsTemplateEditorOpen(true);
    }, []);

    const handleInputChange = useCallback((event) => {
        const { name, value, type, checked } = event.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        if (formError) setFormError('');
    }, [formError]);

    const handleDeleteClick = useCallback((item) => {
        const identifierForArchive = item.id;
        
        const confirmationText = `Are you sure you want to PERMANENTLY DELETE this item: "${item.title}"?\n\nThis action cannot be undone.`;
        
        if (window.confirm(confirmationText)) {
            deleteMutation.mutate({ contentItemId: identifierForArchive });
        }
    }, [deleteMutation]);

    const handleDeleteProtocolClick = useCallback((item) => {
        const confirmationText = `Are you sure you want to delete the Care Bundle "${item.title}"?\n\nThis action cannot be undone.`;
        
        if (window.confirm(confirmationText)) {
            deleteProtocolMutation.mutate({ protocolId: item.id });
        }
    }, [deleteProtocolMutation]);

    const handleSubmitEditMeta = useCallback((event) => {
        event.preventDefault();
        setFormError('');
        let payloadData = { ...formData };
        
        if (typeof payloadData.keywords === 'string') {
          payloadData.keywords = payloadData.keywords.split(',').map(k => k.trim()).filter(Boolean);
        } else if (!Array.isArray(payloadData.keywords)) {
            payloadData.keywords = [];
        }

        let parsedScoringRules = null;
        if (payloadData.scoring_rules_definition && typeof payloadData.scoring_rules_definition === 'string' && payloadData.scoring_rules_definition.trim() !== '') {
            try {
                parsedScoringRules = JSON.parse(payloadData.scoring_rules_definition);
            } catch (e) {
                setFormError("Scoring Rules Definition contains invalid JSON. Please correct it.");
                return;
            }
        }
        const { id, title, keywords, shortDescription, status, source_url, item_metadata, content_type, survey_category, intended_purpose_tags } = payloadData;
        if (!title.trim()) {
            setFormError("Title is required.");
            return;
        }
        const isSurveyTypeContext = content_type === 'questionnaire' || content_type === 'form';
        
        let finalMetadata = item_metadata && Object.keys(item_metadata).length > 0 ? { ...item_metadata } : {};
        if (content_type === 'activity_log_request') {
            // 1. Identify the source of truth (legacy nested vs. current flat)
            const formMeta = finalMetadata.tracker_config ? finalMetadata.tracker_config : finalMetadata;
            const namespace = finalMetadata.tracker_namespace || formMeta.tracker_namespace;

            // 2. Construct the Clean, Flat Metadata Object
            finalMetadata = {
                labels: formMeta.labels || DEFAULT_TRACKER_CONFIG.labels,
                units: formMeta.units || DEFAULT_TRACKER_CONFIG.units,
                goals: formMeta.goals || DEFAULT_TRACKER_CONFIG.goals,
                tracker_namespace: namespace,
                _last_updated: Date.now()
            };

            // 3. Ensure NO legacy nesting exists to confuse future reads
            delete finalMetadata.tracker_config;
            
        } else if (Object.keys(finalMetadata).length > 0) {
             finalMetadata._last_updated = Date.now();
        }

        const metadataToSubmit = {
            title: title.trim(), 
            keywords: keywords,
            intended_purpose_tags: intended_purpose_tags || [],
            short_description: shortDescription?.trim() || null,
            status: status.toLowerCase(), 
            source_url: source_url?.trim() || null,
            item_metadata: Object.keys(finalMetadata).length > 0 ? finalMetadata : undefined,

            ...(isSurveyTypeContext && { survey_category: survey_category || null }),
            ...(isSurveyTypeContext && { scoring_rules_definition: parsedScoringRules }),
        };

        Object.keys(metadataToSubmit).forEach(key => metadataToSubmit[key] === undefined && delete metadataToSubmit[key]);
        logger.debug("Submitting metadata update for item version ID:", id, "Payload:", metadataToSubmit);
        editMetaMutation.mutate({ itemId: id, metadata: metadataToSubmit });
    }, [formData, editMetaMutation]);

    const handleCreateModalNext = useCallback(() => {
        setCreateFormErrors({});
        const errors = {};
        const currentStepName = CREATE_CONTENT_STEPS[createModalStep];
        let currentTitle = (newContentData.title || '').trim();         
        const currentBackendContentType = currentCategoryDetails ? FE_TO_BE_CONTENT_TYPE_MAP[currentCategoryDetails.key] : null;

        if (currentStepName === 'Add Details & Content') {
            if (isDuplicateTitle) {
                errors.title = "This title is already in use. Please choose a unique name.";
                setCreateFormErrors(errors);
                return;
            }
            if (!currentCategoryDetails) {
                errors.general = "Category details missing. Please go back and select a category.";
            } else {
                if (currentCategoryDetails.key === 'BUILD_INTERACTIVE_QUESTIONNAIRE') {
                    if (!currentTitle && (!newContentData.processed_content || newContentData.processed_content.length === 0 || !newContentData.processed_content[0]?.question?.trim())) {
                        errors.title = "Title is required, or design a question to auto-generate the title.";
                    }
                } else {
                     if (currentCategoryDetails.key === 'UPLOAD_EDUCATIONAL_RESOURCE' && selectedFile && !currentTitle) { /* Title auto-derived */ }
                     else if (!currentTitle) { errors.title = "Title is required."; }
                }
                if (currentCategoryDetails.key === 'UPLOAD_EDUCATIONAL_RESOURCE') {
                    if (!selectedFile && !newContentData.source_url.trim()) errors.fileOrUrl = "Either a file upload or a URL is required.";
                    if (newContentData.source_url.trim() && !newContentData.source_url.startsWith('http')) errors.source_url = "Please enter a valid URL.";
                } else if (currentCategoryDetails.key === 'BUILD_INTERACTIVE_QUESTIONNAIRE') {
                    if (!newContentData.processed_content || newContentData.processed_content.length === 0) {
                        errors.processed_content = "Please design your question using the 'Add Element' buttons.";
                    } else {
                        try {
                            validateClientSideInteractiveContent(newContentData.processed_content, 'questionnaire', currentCategoryDetails.key);
                        }
                        catch (e) {
                            errors.processed_content = e.message;
                        }
                    }
                } else if (currentCategoryDetails.usesSeed) {
                    if (!Array.isArray(newContentData.selectedSeedIds) || newContentData.selectedSeedIds.length === 0) {
                        errors.seeds = `Please select at least one question for your ${currentCategoryDetails.label.toLowerCase()}.`;
                    }
                }
            }
        }
        if (Object.keys(errors).length > 0) {
            setCreateFormErrors(errors); return;
        }
        if (createModalStep < CREATE_CONTENT_STEPS.length - 1) {
            setCreateModalStep(prev => prev + 1);
        }
    }, [createModalStep, newContentData, currentCategoryDetails, selectedFile, isDuplicateTitle]);

    const handleSubmitWithLogging = async (action = 'publish') => {
        setCreateFormErrors({});

        if (!currentCategoryDetails) {
            setCreateFormErrors({ general: "Critical error: Content category details were lost. Please restart the process." });
            if (createModalStep !== 0) {
                setCreateModalStep(0);
            }
            return;
        }
        const errors = {};
        let derivedTitle = (newContentData.title || '').trim();
        if (currentCategoryDetails.key === 'BUILD_INTERACTIVE_QUESTIONNAIRE') { 
            
            if (!derivedTitle && newContentData.processed_content && newContentData.processed_content.length > 0 && newContentData.processed_content[0]?.question) {
                const qText = newContentData.processed_content[0].question.trim();
                if (qText) { derivedTitle = qText.length > 70 ? qText.substring(0, 67) + "..." : qText; }
            }
            if (!derivedTitle && (!newContentData.processed_content || newContentData.processed_content.length === 0 || !newContentData.processed_content[0]?.question?.trim())) {
                errors.title = "A title or question text is required for new questions.";
            }
        } else {
            if (currentCategoryDetails.key === 'UPLOAD_EDUCATIONAL_RESOURCE' && selectedFile && !derivedTitle) {
                derivedTitle = selectedFile.name.split('.').slice(0, -1).join('.') || selectedFile.name;
            }
            if (!derivedTitle) {
                errors.title = "Title is required.";
            }
        }
        const backendContentType = FE_TO_BE_CONTENT_TYPE_MAP[currentCategoryDetails.key];
        if (!backendContentType) {
            errors.general = `Invalid content category configuration (no backend type found for ${currentCategoryDetails.key}).`;
            setCreateFormErrors(errors);
            return;
        }
        const isSurveyContentTypeForPayload = backendContentType === 'questionnaire' || backendContentType === 'form';
        if (currentCategoryDetails.key === 'UPLOAD_EDUCATIONAL_RESOURCE') {
            if (!selectedFile && !newContentData.source_url.trim()) {
                errors.fileOrUrl = "Either upload a file or provide a URL.";
            }
            if (newContentData.source_url.trim() && !newContentData.source_url.startsWith('http')) {
                errors.source_url = "Please enter a valid URL.";
            }
        } else if (currentCategoryDetails.key === 'BUILD_INTERACTIVE_QUESTIONNAIRE') {
            try {
                validateClientSideInteractiveContent(newContentData.processed_content, backendContentType, currentCategoryDetails.key);
            } catch (e) {
                errors.processed_content = e.message;
            }
        } else if (currentCategoryDetails.usesSeed) {
            if (!Array.isArray(newContentData.selectedSeedIds) || newContentData.selectedSeedIds.length === 0) {
                errors.seeds = `Please select at least one question.`;
            }
        }

        let parsedScoringRules = null;
        if (newContentData.scoring_rules_definition && typeof newContentData.scoring_rules_definition === 'string' && newContentData.scoring_rules_definition.trim() !== '') {
            try {
                parsedScoringRules = JSON.parse(newContentData.scoring_rules_definition);
            } catch (e) {
                errors.scoring_rules_definition = "Scoring Rules Definition contains invalid JSON.";
            }
        }

        if (Object.keys(errors).length > 0) {
            setCreateFormErrors(errors);
            if (createModalStep !== CREATE_CONTENT_STEPS.indexOf('Add Details & Content')) {
                setCreateModalStep(CREATE_CONTENT_STEPS.indexOf('Add Details & Content'));
            }
            return;
        }
        let finalProcessedContent = null;
        if (currentCategoryDetails.usesSeed) {
            finalProcessedContent = seedQuestions
                .filter(sq => (newContentData.selectedSeedIds || []).includes(sq.id))
                .map(sq => ({
                    id: String(sq.question_id || sq.id),
                    type: String(sq.question_type_system || 'UNKNOWN').toLowerCase(),
                    question: String(sq.question_text || ''),
                    options: sq.answer_options || {},
                }));
            if (finalProcessedContent.length === 0 && (newContentData.selectedSeedIds || []).length > 0) {
                 logger.error("Mismatch: Selected seed IDs but finalProcessedContent is empty.", seedQuestions, newContentData.selectedSeedIds);
                 setCreateFormErrors({ general: "Error processing selected questions. Please try again."});
                 return;
            }

        } else if (currentCategoryDetails.needsEditor) {
            finalProcessedContent = (newContentData.processed_content || []).map(block => {
                const { block_type, type, prompt_text, question, ...restOfBlock } = block;
                return {
                    ...restOfBlock,
                    type: block_type || type,
                    question: prompt_text || question,
                    options: block.options || {},
                };
            });
        }
            
        let trackerConfig = newContentData.item_metadata || {};
        const isTracker = currentCategoryDetails?.isTracker || backendContentType === 'activity_log_request';

        // 1. If it's a tracker and metadata is empty, inject defaults.
        if (isTracker && Object.keys(trackerConfig).length === 0) {
            trackerConfig = JSON.parse(JSON.stringify(DEFAULT_TRACKER_CONFIG));
        }
        
        // 2. Build the final item_metadata structure for submission
        let finalMetadata = {};
        
        if (isTracker) {
            finalMetadata = { 
                ...newContentData.item_metadata, 
                tracker_namespace: newContentData.item_metadata?.tracker_namespace 
            };
        } else {
            finalMetadata = { ...newContentData.item_metadata };
        }
        if (isTracker && !finalMetadata.tracker_namespace) {
            const timestamp = Date.now().toString(36);
            const safeTitle = (derivedTitle || 'tracker').toLowerCase().replace(/[^a-z0-9]/g, '_').slice(0, 20);
            finalMetadata.tracker_namespace = `trk_${timestamp}_${safeTitle}`;
        }
        const basePayload = {
            title: newContentData.title,
            content_type: backendContentType,
            keywords: newContentData.keywords,
            description: newContentData.shortDescription?.trim() || null,
            processed_content: finalProcessedContent, 
            survey_category: isSurveyContentTypeForPayload ? (newContentData.survey_category || null) : null,
            scoring_rules_definition: isSurveyContentTypeForPayload ? parsedScoringRules : null,
            source_url: newContentData.source_url.trim() || null,
            status: action === 'publish' ? 'active' : 'draft',
            item_metadata: Object.keys(finalMetadata).length > 0 ? finalMetadata : undefined,
        };
        Object.keys(basePayload).forEach(key => basePayload[key] === undefined && delete basePayload[key]);
        const formDataInstance = new FormData();
        formDataInstance.append('metadata', JSON.stringify(basePayload));
        if (selectedFile) {
            formDataInstance.append('file', selectedFile);
        }
        const submissionPayload = formDataInstance;

        if (submissionPayload instanceof FormData) {
            for (let [key, value] of submissionPayload.entries()) {
            }
        } else {
        }
        addMutation.mutate(submissionPayload);
    };

    const handleDuplicateItem = useCallback((itemToDuplicate) => {
        logger.info(`Duplicating item: ${itemToDuplicate.title} (Type: ${itemToDuplicate.type})`);

        if (itemToDuplicate.type === 'Care Bundle' || itemToDuplicate.type === 'Med Protocol') {
        const bundleCopy = JSON.parse(JSON.stringify(itemToDuplicate));
        
        bundleCopy.id = null; 
        bundleCopy.name = `${itemToDuplicate.title} (Copy)`;
        
        setDuplicateBundleData(bundleCopy); 
        setIsBundleEditMode(false); 
        setIsTemplateEditorOpen(true); 

        } else if (itemToDuplicate.type === 'Content Item') {
            const categoryKey = Object.keys(FE_TO_BE_CONTENT_TYPE_MAP).find(key =>
                FE_TO_BE_CONTENT_TYPE_MAP[key] === itemToDuplicate.content_type
            ) || '';
            setNewContentData({
                ...initialNewContentData,
                ...itemToDuplicate,
                categoryKey: categoryKey,
                status: 'DRAFT', 
                id: null, // Ensure content items also don't overwrite
                version: 1
            });
            setCreateModalStep(0);
            setIsCreateModalOpen(true);

        } else if (itemToDuplicate.type === 'Program') {
            const fullProgramData = allPrograms.find(p => p.id === itemToDuplicate.id);
            if (!fullProgramData) {
                alert("Error: Could not find program data to duplicate.");
                return;
            }
            const programCopy = {
                ...fullProgramData,
                name: `Copy of ${fullProgramData.name}`,
                id: null 
            };
            setProgramToEdit(programCopy); 
            setIsProgramBuilderOpen(true);
            
        } else {
            alert(`Duplication for item type "${itemToDuplicate.type}" is not yet implemented.`);
        }
    }, [initialNewContentData, allPrograms]);

    const handleDownloadPdf = useCallback(async (item) => {
        const printableElementId = 'pdf-render-target';
        let container = document.getElementById(printableElementId);

        if (!container) {
            container = document.createElement('div');
            container.id = printableElementId;
            container.style.position = 'absolute';
            container.style.left = '-9999px';
            container.style.top = 'auto';
            container.style.width = '800px';
            document.body.appendChild(container);
        }

        ReactDOM.render(
            <Paper sx={{ p: 4 }}>
                <ReadOnlySurveyRenderer
                    surveyTitle={item.title}
                    surveyDescription={item.short_description}
                    blocks={item.processed_content || []}
                    patientResponses={{}}
                />
            </Paper>,
            container,
            async () => {
                try {
                    const canvas = await html2canvas(container, {
                        scale: 2,
                        useCORS: true,
                    });
                    const imgData = canvas.toDataURL('image/png');
                    const pdf = new jsPDF({
                        orientation: 'p',
                        unit: 'px',
                        format: [canvas.width, canvas.height],
                    });
                    pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
                    pdf.save(`${item.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`);
                } catch (e) {
                    logger.error("PDF generation failed:", e);
                } finally {
                    ReactDOM.unmountComponentAtNode(container);
                    if (container.parentNode) {
                        container.parentNode.removeChild(container);
                    }
                }
            }
        );
    }, []);

    const handleDeleteProgramClick = useCallback((item) => {
        const confirmationText = `Are you sure you want to PERMANENTLY DELETE the program: "${item.title}"?\n\nThis action cannot be undone.`;
        
        if (window.confirm(confirmationText)) {
            deleteProgramMutation.mutate({ programId: item.id }, {
                // OVERRIDE: Explicitly handle error here since no modal is open to show the message
                onError: (error) => {
                    const detail = error?.response?.data?.detail || error?.data?.detail || error.message || "An unknown error occurred.";
                    // This will display: "Unable to delete: This program is currently assigned to X patient(s)..."
                    alert(detail);
                }
            });
        }
    }, [deleteProgramMutation]);

    const handleOpenCreateProgram = () => {
        setProgramToEdit(null);
        setIsProgramBuilderOpen(true);
    };

    const handleOpenEditProgram = (programItem) => {
        setProgramToEdit(programItem);
        setIsProgramBuilderOpen(true);
    };

    const handleCloseProgramBuilder = () => {
        setIsProgramBuilderOpen(false);
        setProgramToEdit(null);
    };

    const handleEditTrackerConfigChange = useCallback((newConfig) => {
        setFormData(prev => ({
            ...prev,
            item_metadata: newConfig
        }));
    }, []);

    const handleTrackerConfigChange = useCallback((newConfig) => {
        setNewContentData(prev => ({
            ...prev,
            item_metadata: newConfig
        }));
    }, []);

    const renderCreateModalStepContent = () => {
        const currentStepName = CREATE_CONTENT_STEPS[createModalStep];
        const currentBackendContentType = currentCategoryDetails ? FE_TO_BE_CONTENT_TYPE_MAP[currentCategoryDetails.key] : null;
        const isSurveyLikeContentDisplay = currentBackendContentType === 'questionnaire' || currentBackendContentType === 'form';
        
        switch (currentStepName) {
            case 'Add Details & Content':
                if (!currentCategoryDetails) {
                     return <Alert severity="error">Content category not selected. Please close and try again.</Alert>;
                }
                return (
                    <Box>
                        <TextField
                            fullWidth required autoFocus margin="normal" label="Topic" name="title"
                            value={newContentData.title} 
                            onChange={handleNewContentInputChange}
                            error={!!createFormErrors.title || isDuplicateTitle}
                            helperText={
                                isDuplicateTitle 
                                    ? "⚠️ A content item with this title already exists." 
                                    : (createFormErrors.title || "A descriptive high-level Topic for this content")
                            }
                            placeholder={currentCategoryDetails?.key === 'BUILD_INTERACTIVE_QUESTIONNAIRE' ? "e.g., Pre-op Checklist, Pain Scale" : "Enter Topic"}
                        />
                         {currentCategoryDetails.usesSeed && (
                            <Box mt={0} mb={2} >
                                <Typography variant="subtitle1" gutterBottom>Select Questions for your "{currentCategoryDetails.label}"</Typography>
                                {isLoadingSeedQuestions ? <CircularProgress /> : (
                                    seedQuestions.length > 0 ? (
                                        <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto', p:1, borderColor: createFormErrors.seeds ? 'error.main' : 'divider' }}>
                                            <List dense>
                                                {seedQuestions.map(seed => (
                                                    <ListItem key={seed.id} dense disablePadding
                                                        secondaryAction={
                                                            <Checkbox
                                                                edge="end"
                                                                onChange={() => handleSeedQuestionToggle(seed.id)}
                                                                checked={(newContentData.selectedSeedIds || []).includes(seed.id)}
                                                            />
                                                        }
                                                    >
                                                        <ListItemText
                                                            primary={seed.question_text || 'Unnamed Question'}
                                                            secondary={`Type: ${seed.question_type_system} | Categories: ${(seed.category_tags || []).join(', ')}`}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Paper>
                                    ) : <Typography sx={{my:2, fontStyle:'italic'}}>No seed questions available. You might need to "Contribute New Questions" first.</Typography>
                                )}
                                {createFormErrors.seeds && <FormHelperText error>{createFormErrors.seeds}</FormHelperText>}
                            </Box>
                        )}
                        {currentCategoryDetails.isTracker && (
                             <Box sx={{ mt: 3, mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: 'grey.50' }}>
                                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', mb: 1 }}>
                                    Customize Tracker Fields
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Configure the labels and units for this activity log. These settings will appear on the patient dashboard and charts.
                                </Typography>
                                {/* INSERT THIS COMPONENT 👇 */}
                                <ActivityTrackerEditor
                                    config={newContentData.item_metadata}
                                    onChange={handleTrackerConfigChange}
                                />
                             </Box>
                        )}
                        {currentCategoryDetails.key === 'UPLOAD_EDUCATIONAL_RESOURCE' && (
                            <Grid container spacing={2} alignItems="center" sx={{ mt: 0, mb: 1 }}>
                                <Grid item xs={12} sm={7}>
                                    <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} fullWidth sx={{ height: '56px', textTransform: 'none' }}>
                                        Upload File (Max {MAX_FILE_SIZE_MB}MB)
                                        <input type="file" hidden onChange={handleFileChange} accept={ACCEPT_FILE_TYPES} ref={fileInputRef} />
                                    </Button>
                                    {selectedFile && <Typography variant="caption" display="block" sx={{ mt: 0.5, textAlign: 'center' }}>Selected: {selectedFile.name}</Typography>}
                                    {fileError && <FormHelperText error sx={{ textAlign: 'center', mt: 0.5 }}>{fileError}</FormHelperText>}
                                </Grid>
                                <Grid item xs={12} sm={1} sx={{textAlign: 'center'}}> <Typography variant="body2" color="text.secondary">OR</Typography> </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth label="Enter URL" name="source_url" value={newContentData.source_url}
                                        onChange={handleNewContentInputChange} error={!!createFormErrors.source_url}
                                        helperText={createFormErrors.source_url || "e.g., YouTube, Vimeo link"}
                                        placeholder="https://..."
                                        InputProps={{ startAdornment: <InputAdornment position="start"><LinkIcon /></InputAdornment> }}
                                    />
                                </Grid>
                                {createFormErrors.fileOrUrl &&
                                    <Grid item xs={12} sx={{pt: "8px !important"}}>
                                        <Alert severity="error" sx={{ width: '100%', p:0.5, fontSize: '0.75rem'  }}>{createFormErrors.fileOrUrl}</Alert>
                                    </Grid>
                                }
                            </Grid>
                        )}

                        {currentCategoryDetails.needsEditor && (
                            <Box sx={{ mt: 3, mb: 2, p: 2, border: '1px solid', borderColor: createFormErrors.processed_content ? 'error.main' : 'divider', borderRadius: 1.5 }}>
                                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', mb: 2 }}>
                                    Design Your Question
                                </Typography>
                                <VisualContentEditor
                                    blocks={newContentData.processed_content || []}
                                    onChange={handleNewVisualContentChange}
                                />
                                {createFormErrors.processed_content && (
                                    <FormHelperText error sx={{ mt: 1.5, ml: 0.5 }}>
                                        {createFormErrors.processed_content}
                                    </FormHelperText>
                                )}
                            </Box>
                        )}

                        <TextField
                            fullWidth margin="normal" multiline rows={3}
                            label="Add a short description or instructions about the content here (Optional)"
                            name="shortDescription"
                            value={newContentData.shortDescription}
                            onChange={handleNewContentInputChange}
                            inputProps={{ maxLength: 250 }}
                            helperText={`${(newContentData.shortDescription || '').length}/250 characters. Examples: "Introductory video for new patients.", "Weekly pain log for post-op recovery."`}
                            error={!!createFormErrors.shortDescription}
                        />

                        {isSurveyLikeContentDisplay && (
                            <>
                                <FormControl fullWidth margin="normal" error={!!createFormErrors.survey_category}>
                                    <InputLabel id="survey-category-label">Survey Category</InputLabel>
                                    <Select
                                        labelId="survey-category-label" id="survey_category" name="survey_category"
                                        value={newContentData.survey_category} label="Survey Category"
                                        onChange={handleNewContentInputChange}
                                    >
                                        <MenuItem value=""><em>None / Not Applicable</em></MenuItem>
                                        {SURVEY_CATEGORY_OPTIONS.map(option => (
                                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                        ))}
                                    </Select>
                                    {createFormErrors.survey_category && <FormHelperText>{createFormErrors.survey_category}</FormHelperText>}
                                </FormControl>
                            </>
                        )}

                        <Autocomplete
                            multiple freeSolo fullWidth
                            options={currentCategoryDetails?.defaultKeywords || []}
                            value={newContentData.keywords}
                            onChange={(event, newValue) => handleNewContentMultiSelectChange('keywords', newValue)}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => {
                                    const { key, ...tagProps } = getTagProps({ index });
                                    return <Chip key={key} variant="outlined" label={option} {...tagProps} size="small" />;
                                })
                            }
                            // Change label and helperText:
                            renderInput={(params) => (
                                <TextField
                                    {...params} variant="outlined" margin="normal"
                                    label="Keywords (Optional)" 
                                    placeholder="Add or edit relevant keywords"
                                    error={!!createFormErrors.keywords}
                                    helperText={createFormErrors.keywords || "Add keywords to help searches."}
                                />
                            )}
                        />

                        {isSurveyLikeContentDisplay && currentCategoryDetails?.key !== 'BUILD_INTERACTIVE_QUESTIONNAIRE' && (
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Scoring Rules Definition (JSON - Advanced)"
                                name="scoring_rules_definition_text"
                                value={ typeof newContentData.scoring_rules_definition === 'string' ? newContentData.scoring_rules_definition : JSON.stringify(newContentData.scoring_rules_definition || {}, null, 2) }
                                onChange={(e) => {
                                    let parsedJsonOrString = e.target.value;
                                    try {
                                        if (e.target.value.trim() === '') {
                                            parsedJsonOrString = null;
                                        } else {
                                            parsedJsonOrString = JSON.parse(e.target.value);
                                        }
                                        setNewContentData(prev => ({ ...prev, scoring_rules_definition: parsedJsonOrString }));
                                        if (createFormErrors.scoring_rules_definition) {
                                            setCreateFormErrors(prev => ({...prev, scoring_rules_definition: null}));
                                        }
                                    } catch (jsonError) {
                                        setNewContentData(prev => ({ ...prev, scoring_rules_definition: e.target.value }));
                                        setCreateFormErrors(prev => ({...prev, scoring_rules_definition: "Invalid JSON format."}));
                                    }
                                }}
                                multiline
                                rows={2}
                                variant="outlined"
                                placeholder='e.g., {"score_mapping": {"option_1": 1, "option_2": 2}}'
                                helperText={createFormErrors.scoring_rules_definition || "Optional: Define scoring logic as JSON. Usually set by admins."}
                                error={!!createFormErrors.scoring_rules_definition}
                                sx={{ mt: 1, mb: 1 }}
                            />
                        )}
                    </Box>
                );

            case 'Review & Finalize':
                let reviewTitle = newContentData.title.trim();
                if (currentCategoryDetails?.key === 'BUILD_INTERACTIVE_QUESTIONNAIRE') {
                    if (!reviewTitle && newContentData.processed_content && newContentData.processed_content.length > 0 && newContentData.processed_content[0]?.question) {
                        const qText = newContentData.processed_content[0].question.trim();
                        if (qText) { reviewTitle = qText.length > 70 ? qText.substring(0, 67) + "..." : qText; }
                    }
                    if (!reviewTitle) { reviewTitle = currentCategoryDetails.defaultTitlePrefix ? currentCategoryDetails.defaultTitlePrefix + "Untitled Question" : "Untitled Question"; }
                } else if (currentCategoryDetails?.key === 'UPLOAD_EDUCATIONAL_RESOURCE' && selectedFile && !reviewTitle) {
                     reviewTitle = selectedFile.name.split('.').slice(0, -1).join('.') || selectedFile.name;
                }
                const finalReviewContentType = FE_TO_BE_CONTENT_TYPE_MAP[currentCategoryDetails?.key || ''] || currentCategoryDetails?.type || 'N/A';
                let contentSummary = ``;
                if (currentCategoryDetails?.usesSeed) {
                    contentSummary = `${(newContentData.selectedSeedIds || []).length} questions selected.`;
                } else if (currentCategoryDetails?.needsEditor) {
                    contentSummary = `${(newContentData.processed_content || []).length} custom blocks/questions designed.`;
                } else if (currentCategoryDetails?.key === 'UPLOAD_EDUCATIONAL_RESOURCE') {
                    contentSummary = selectedFile ? `File: ${selectedFile.name}` : (newContentData.source_url || 'No file or URL provided');
                }
                return (
                    <Box>
                        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="h6" gutterBottom sx={{mb: 2, textAlign: 'center' }}>Review Your Content</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Box mb={1.5}><Typography variant="caption" color="text.secondary" display="block">TITLE</Typography><Typography variant="body1">{reviewTitle || "(Not Set)"}</Typography></Box>
                                    <Box mb={1.5}><Typography variant="caption" color="text.secondary" display="block">TYPE</Typography><Typography variant="body1">{currentCategoryDetails?.label || "N/A"}</Typography></Box>
                                    <Box mb={1.5}><Typography variant="caption" color="text.secondary" display="block">DESCRIPTION</Typography><Typography variant="body1" sx={{wordBreak: 'break-word'}}>{newContentData.shortDescription || "N/A"}</Typography></Box>
                                    {isSurveyLikeContentDisplay && (
                                        <Box mb={1.5}>
                                            <Typography variant="caption" color="text.secondary" display="block">SURVEY CATEGORY</Typography>
                                            <Typography variant="body1">{(SURVEY_CATEGORY_OPTIONS.find(opt => opt.value === newContentData.survey_category)?.label) || "Not Specified"}</Typography>
                                        </Box>
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Box mb={1.5}><Typography variant="caption" color="text.secondary" display="block">KEYWORDS</Typography><Typography variant="body1" sx={{wordBreak: 'break-word'}}>{(newContentData.keywords || []).join(', ') || "N/A"}</Typography></Box>
                                    <Box mb={1.5}><Typography variant="caption" color="text.secondary" display="block">CONTENT DETAILS</Typography><Typography variant="body1">{contentSummary}</Typography></Box>
                                    <Box mb={1.5}><Typography variant="caption" color="text.secondary" display="block">BACKEND TYPE</Typography><Typography variant="body1">{finalReviewContentType}</Typography></Box>
                                </Grid>
                            </Grid>
                        </Paper>
                        {createFormErrors.general && <Alert severity="error" sx={{mt:1}}>{createFormErrors.general}</Alert>}
                    </Box>
                );
            default: return <Typography>Unknown step</Typography>;
        }
    };

    const renderEditModalContent = () => {
        if (!selectedItem || !formData) return <CircularProgress />;
        const displayKeywords = Array.isArray(formData.keywords) ? formData.keywords.join(', ') : (formData.keywords || '');
        const isSurveyTypeForEdit = formData.content_type === 'questionnaire' || formData.content_type === 'form';
        const isTrackerType = formData.content_type === 'activity_log_request';

        return (
            <form onSubmit={handleSubmitEditMeta}>
                <DialogContent dividers sx={{pt:2}}>
                    <TextField fullWidth margin="normal" label="Title" name="title" value={formData.title || ''} onChange={handleInputChange} required />
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                             <TextField
                                fullWidth margin="normal" multiline rows={3}
                                label="Short Description / Instructions (Optional)"
                                name="shortDescription"
                                value={formData.shortDescription || ''}
                                onChange={handleInputChange}
                                inputProps={{ maxLength: 250 }}
                                helperText={`${(formData.shortDescription || '').length}/250 characters`}
                            />
                        </Grid>
                        {isTrackerType && (
                            <Grid item xs={12}>
                                <Box sx={{ mt: 2, mb: 2, border: '1px solid #ddd', borderRadius: 2, p: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>Tracker Configuration</Typography>
                                    <ActivityTrackerEditor 
                                        config={formData.item_metadata}
                                        onChange={handleEditTrackerConfigChange} 
                                    />
                                </Box>
                            </Grid>
                        )}
                        {isSurveyTypeForEdit && (
                            <>
                                <Grid item xs={12}>
                                    <FormControl fullWidth margin="normal">
                                        <InputLabel id="edit-survey-category-label">Survey Category</InputLabel>
                                        <Select
                                            labelId="edit-survey-category-label" name="survey_category"
                                            value={formData.survey_category || ''} label="Survey Category"
                                            onChange={handleInputChange}
                                        >
                                            <MenuItem value=""><em>None / Not Applicable</em></MenuItem>
                                            {SURVEY_CATEGORY_OPTIONS.map(option => (
                                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </>
                        )}
                        <Grid item xs={12}>
                            <TextField 
                                fullWidth margin="normal" 
                                label="Keywords (comma-separated)" 
                                name="keywords" 
                                value={displayKeywords} 
                                onChange={handleInputChange} 
                                helperText="Optional. Separate with commas." 
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete
                                multiple
                                freeSolo
                                options={['cataract_surgery', 'pre_operative', 'post_operative']}
                                value={formData.intended_purpose_tags || []}
                                onChange={(event, newValue) => {
                                    setFormData(prev => ({ ...prev, intended_purpose_tags: newValue }));
                                }}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => {
                                        const { key, ...tagProps } = getTagProps({ index });
                                        return <Chip key={key} variant="outlined" label={option} {...tagProps} />;
                                    })
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        margin="normal"
                                        label="Purpose Tags (for Pre-Op Journeys)"
                                        helperText="Links content to procedures (e.g., 'cataract_surgery'). Press Enter to add a new tag."
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={isSurveyTypeForEdit ? 12 : 6}>
                            <FormControl fullWidth margin="normal">
                                <InputLabel id="status-label-edit">Status</InputLabel>
                                <Select labelId="status-label-edit" name="status" value={formData.status || 'DRAFT'} onChange={handleInputChange} label="Status">
                                    <MenuItem value="ACTIVE">Active</MenuItem>
                                    <MenuItem value="DRAFT">Draft</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {formData.content_type === 'education' && !isSurveyTypeForEdit && (
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth margin="normal" label="Source URL" name="source_url" value={formData.source_url || ''} onChange={handleInputChange} />
                            </Grid>
                        )}

                        {isSurveyTypeForEdit && (
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    label="Scoring Rules Definition (JSON)"
                                    name="scoring_rules_definition"
                                    multiline
                                    rows={6}
                                    value={formData.scoring_rules_definition || ''}
                                    onChange={handleInputChange}
                                    helperText="Define scoring logic as a JSON object. Ensure valid JSON."
                                    variant="outlined"
                                />
                            </Grid>
                        )}
                    </Grid>
                    {formError && <Alert severity="error" sx={{mt: 2}}>{formError}</Alert>}
                </DialogContent>
                <DialogActions sx={{p:2, borderTop: `1px solid ${theme.palette.divider}`}}>
                    <Button onClick={handleModalClose} sx={{ mr: 'auto' }}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={isMutating()} startIcon={isMutating() ? <CircularProgress size={20}/> : <SaveIcon /> }>
                        Save Changes
                    </Button>
                </DialogActions>
            </form>
        );
    };

    const handleSttSearch = () => {
        if (!sttSupported || !recognitionRef.current) {
            const errorMessage = sttSupported
                ? "STT not initialized. Please refresh."
                : "Speech recognition is not supported in this browser.";
            setSttError(errorMessage);
            logger.warn(`STT: Attempted to use STT. Supported: ${sttSupported}, Initialized: ${!!recognitionRef.current}`);
            return;
        }

        if (isListening) {
            recognitionRef.current.stop();
            logger.info("STT: Stop listening requested by user.");
        } else {
            setSttError('');
            try {
                recognitionRef.current.start();
                logger.info("STT: Start listening requested by user.");
            } catch (e) {
                logger.error("STT: Error calling recognition.start()", e);
                setSttError("Could not start speech recognition. Please try again.");
                setIsListening(false);
            }
        }
    };

    if (isAuthContextLoading || (isLoadingContent && !debouncedSearchTerm && !allContentItems.length && !queryError)) {
        return <Box sx={{display:'flex', justifyContent:'center', alignItems:'center', height: 'calc(100vh - 200px)'}}><CircularProgress /></Box>;
    }
    if (queryError && !allContentItems.length) {
         return <Container sx={{mt:3}}><Alert severity="error">Error loading content items: {queryError.message}</Alert></Container>;
    }

    return (
        <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Typography component="h1" variant="h4" sx={{ fontWeight: 'medium' }}> Content Studio </Typography>
                {canManageContent && (
                    
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button 
                        component="label" 
                        variant="outlined" 
                        startIcon={isBulkUploading ? <CircularProgress size={20} /> : <DriveFolderUploadIcon />} 
                        disabled={isMutating() || isAiCreating || isBulkUploading}
                        sx={{ borderStyle: 'dashed' }} 
                    >
                        {isBulkUploading ? `Uploading ${Math.round(bulkProgress)}%` : "Bulk Import"}
                        <input 
                            type="file" 
                            hidden 
                            multiple 
                            onChange={handleBulkFileChange} 
                            accept={ACCEPT_FILE_TYPES} 
                        />
                    </Button>
                    <Button variant="outlined" startIcon={<CloudUploadIcon />} onClick={handleOpenUploadResourceModal} disabled={isMutating() || isAiCreating}>
                        Upload Resource
                    </Button>
                    
                    {/* EXISTING BUTTON */}
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={handleOpenCreateQuestionnaireModal} disabled={isMutating() || isAiCreating}>
                        Build Materials
                    </Button>

                    {/* --- ADD THIS NEW BUTTON --- */}
                    <Button variant="outlined" startIcon={<TrackChangesIcon />} onClick={handleOpenCreateTrackerModal} disabled={isMutating() || isAiCreating}>
                        Design Tracker
                    </Button>
                    {/* --------------------------- */}

                    <Button variant="outlined" startIcon={<PlaylistAddCheckIcon />} onClick={() => setIsTemplateEditorOpen(true)} disabled={isMutating() || isAiCreating}>
                        Build Care Bundles
                    </Button>
                    <Button variant="contained" startIcon={<AccountTreeIcon />} onClick={handleOpenCreateProgram} disabled={isMutating() || isAiCreating}>
                        Build / Edit Programs
                    </Button>
                        <Button
                            component="label"
                            variant="contained"
                            startIcon={isAiCreating ? <CircularProgress size={20} color="inherit" /> : <i className="fas fa-magic" style={{ marginRight: '8px' }}></i>}
                            disabled={isMutating() || isAiCreating}
                        >
                            AI-Build Material from PDF
                            <input type="file" hidden accept="application/pdf" onChange={handleCreateWithAi} />
                        </Button>
                    </Box>
                )}
            </Box>
            {/* --- NEW PROGRESS BAR --- */}
            {isBulkUploading && (
                <Box sx={{ width: '100%', mb: 2 }}>
                    <LinearProgress variant="determinate" value={bulkProgress} />
                    <Typography variant="caption" sx={{ textAlign: 'center', display: 'block', mt: 0.5 }}>
                        Processing files... please wait.
                    </Typography>
                </Box>
            )}

            <Paper sx={{ p: {xs: 1.5, sm: 2}, mb: 3, borderRadius: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Search Content"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip
                                            title={
                                                !sttSupported
                                                    ? "Speech recognition not supported"
                                                    : isListening
                                                        ? "Stop Listening"
                                                        : "Search by Voice (STT)"
                                            }
                                        >
                                            <span>
                                                <IconButton
                                                    onClick={handleSttSearch}
                                                    size="small"
                                                    color={isListening ? "primary" : "default"}
                                                    aria-label="search-by-voice"
                                                    disabled={!sttSupported}
                                                >
                                                    <MicIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </InputAdornment>
                                )
                            }}
                        />
                        <FormHelperText sx={{ml:0.5, display: 'block'}}>
                            Searches title, keywords, and description.
                        </FormHelperText>
                        {sttError && (
                            <FormHelperText error sx={{ml: 0.5, mt: 0.25, display: 'block'}}>
                                {sttError}
                            </FormHelperText>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={activeTab === 'CONTENT_ITEMS' ? 2 : 3}> {/* Adjust width */}
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                                {STATUS_FILTER_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt.replace(/_/g, ' ')}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* ADD THIS: Content Type Filter (Conditional) */}
                    {activeTab === 'CONTENT_ITEMS' && (
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Content Type</InputLabel>
                                <Select
                                    value={contentTypeFilter}
                                    label="Content Type"
                                    onChange={(e) => setContentTypeFilter(e.target.value)}
                                >
                                    {CONTENT_TYPE_FILTER_OPTIONS.map(opt => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={activeTab === 'CONTENT_ITEMS' ? 3 : 5}> {/* Adjust width */}
                        <FormControlLabel control={<Checkbox checked={showAllVersions} onChange={(e) => setShowAllVersions(e.target.checked)} />} label="Show All Versions" />
                    </Grid>
                </Grid>
            </Paper>

            {isLoadingContent && debouncedSearchTerm && <Box sx={{display:'flex', justifyContent:'center', p:3}}><CircularProgress /></Box>}
            {!isLoadingContent && queryError && allContentItems.length > 0 &&
                <Alert severity="warning" sx={{mb:2}}>Could not refresh content: {queryError.message}</Alert>
            }

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs
                    value={activeTab}
                    onChange={(event, newValue) => setActiveTab(newValue)}
                    aria-label="Asset Type Tabs"
                >
                    {TABS.map((tab) => (
                        <Tab key={tab.value} label={tab.label} value={tab.value} />
                    ))}
                </Tabs>
            </Box>

            <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2, boxShadow: theme.shadows[2] }}>
                <Table stickyHeader aria-label="content items table">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{fontWeight:'bold', width: '25%'}}>Title</TableCell>
                            <TableCell sx={{fontWeight:'bold'}}>Type</TableCell>
                            {(activeTab === 'ALL' || activeTab === 'CONTENT_ITEMS') && (
                                <TableCell sx={{fontWeight:'bold'}}>Survey Category</TableCell>
                            )}
                            <TableCell sx={{fontWeight:'bold', textAlign:'center'}}>Version</TableCell>
                            <TableCell sx={{fontWeight:'bold'}}>Status</TableCell>
                            <TableCell sx={{fontWeight:'bold', minWidth: 150}}>Keywords</TableCell>
                            <TableCell sx={{fontWeight:'bold'}}>Last Updated</TableCell>
                            <TableCell sx={{fontWeight:'bold', textAlign:'right', pr: {xs:1, sm:2}}}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {itemsForCurrentTab.map((item) => (
                            <TableRow key={item.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 }, opacity: item.status === 'archived' || item.status === 'superseded' ? 0.65 : 1 }}>
                                <TableCell component="th" scope="row">
                                    <Box sx={{display:'flex', alignItems:'center'}}>
                                        {getIconForContentType(item)}
                                        <Tooltip title={item.short_description || item.sub_specialty || ''}>
                                            <Typography variant="body2" component="span" sx={{cursor: 'default'}}>{item.title}</Typography>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                                <TableCell><Chip label={item.type || 'N/A'} size="small" variant="outlined" /></TableCell>
                                {(activeTab === 'ALL' || activeTab === 'CONTENT_ITEMS') && (
                                    <TableCell>{(SURVEY_CATEGORY_OPTIONS.find(opt => opt.value === item.survey_category)?.label) || ((item.type === 'Content Item' && (item.content_type === 'questionnaire' || item.content_type === 'form')) ? 'N/A' : '')}</TableCell>
                                )}
                                <TableCell sx={{textAlign:'center'}}>V{item.version}</TableCell>
                                <TableCell>
                                    <Chip label={item.status?.toUpperCase() || 'UNKNOWN'} size="small"
                                          color={item.status === 'active' ? 'success' : item.status === 'draft' ? 'info' : (item.status === 'archived' || item.status === 'superseded' ? 'default' : 'warning')}
                                          variant={item.status === 'archived' || item.status === 'superseded' ? 'outlined' : 'filled'}
                                          sx={{fontWeight:'medium', textTransform:'capitalize'}}
                                          />
                                </TableCell>
                                <TableCell sx={{maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                    <Tooltip title={(item.keywords || []).join(', ')}>
                                        <span>{(item.keywords || []).join(', ')}</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>{new Date(item.updated_at).toLocaleDateString()}</TableCell>
                                <TableCell sx={{textAlign:'right'}}>
                                     <Tooltip title="View">
                                        <span>
                                            <IconButton size="small" onClick={() => handleOpenViewModal(item)} disabled={isMutating()}>
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                            
                                    {item.type === 'Program' ? (
                                        <Tooltip title="Edit Program">
                                            <span>
                                                <IconButton size="small" onClick={() => handleOpenEditProgram(item)} disabled={isMutating() || !canManageContent}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    ) : item.type === 'Care Bundle' || item.type === 'Med Protocol' ? (
                                        <Tooltip title="Edit Bundle">
                                            <span>
                                                <IconButton size="small" onClick={() => handleOpenEditBundleModal(item)} disabled={isMutating() || !canManageContent}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip title="Edit Metadata">
                                            <span>
                                                <IconButton size="small" onClick={() => handleOpenEditMetaModal(item)} disabled={isMutating() || !canManageContent}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}

                                     <Tooltip title="Duplicate">
                                        <span>
                                            <IconButton size="small" onClick={() => handleDuplicateItem(item)} disabled={isMutating() || !canManageContent}>
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    {(item.content_type === 'questionnaire' || item.content_type === 'form') && (
                                        <Tooltip title="Download as PDF">
                                            <span>
                                                <IconButton size="small" onClick={() => handleDownloadPdf(item)} disabled={isMutating()}>
                                                    <PictureAsPdfIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                    {item.is_latest_version && (
                                        <Tooltip title="Permanently Delete This Series">
                                            <span>
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => {
                                                        if (item.type === 'Content Item') handleDeleteClick(item);
                                                        else if (item.type === 'Program') handleDeleteProgramClick(item);
                                                        else handleDeleteProtocolClick(item);
                                                    }}
                                                    disabled={isMutating() || !canManageContent}
                                                    color="error"
                                                >
                                                    <DeleteForeverIcon fontSize="small"/>
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                        {itemsForCurrentTab.length === 0 && !isLoadingContent && (
                            // CHANGE colSpan value
                            <TableRow><TableCell colSpan={(activeTab === 'ALL' || activeTab === 'CONTENT_ITEMS') ? 8 : 7} align="center">No items found matching your criteria.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Dialog open={isCreateModalOpen} onClose={handleCreateModalClose} maxWidth="md" fullWidth TransitionProps={{ unmountOnExit: true }}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2, pb: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'medium' }}>
                        {CREATE_CONTENT_STEPS[createModalStep]}: {currentCategoryDetails?.label || "Create New Content"}
                    </Typography>
                    <IconButton aria-label="close" onClick={handleCreateModalClose}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers={createModalStep === CREATE_CONTENT_STEPS.indexOf('Add Details & Content')} sx={{ minHeight: {xs: '70vh', md: '60vh'}, pt: createModalStep === 0 ? 2 : 0 }}>
                    <Stepper activeStep={createModalStep} alternativeLabel sx={{ mb: 3, mt: createModalStep === 0 ? 1 : 2.5 }}>
                        {CREATE_CONTENT_STEPS.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
                    </Stepper>
                    {renderCreateModalStepContent()}
                    {createFormErrors.general && <Alert severity="error" sx={{mt:2, mx: createModalStep !== CREATE_CONTENT_STEPS.indexOf('Add Details & Content') ? 2 : 0 }}>{createFormErrors.general}</Alert>}
                </DialogContent>
                <DialogActions sx={{p:2, borderTop: `1px solid ${theme.palette.divider}`}}>
                    <Button onClick={handleCreateModalClose} disabled={isMutating()} sx={{ mr: 'auto' }}>Cancel</Button>
                    <Box>
                        {createModalStep > 0 &&
                            <Button onClick={handleCreateModalBack} disabled={isMutating()} sx={{mr:1}}>Back</Button>
                        }
                        {createModalStep < CREATE_CONTENT_STEPS.length - 1 ?
                            <Button onClick={handleCreateModalNext} variant="contained" disabled={isMutating() || !newContentData.categoryKey}>
                                Next
                            </Button>
                        : (
                            <>
                                <Button
                                    onClick={() => handleSubmitWithLogging('draft')}
                                    variant="outlined"
                                    disabled={isMutating()}
                                    sx={{mr:1}}
                                    startIcon={addMutation.isPending && addMutation.variables?.status === 'draft' ? <CircularProgress size={20} color="inherit"/> : null}
                                >
                                    Save as Draft
                                </Button>
                                <Button
                                    onClick={() => handleSubmitWithLogging('publish')}
                                    variant="contained"
                                    disabled={isMutating()}
                                    startIcon={addMutation.isPending && addMutation.variables?.status === 'active' ? <CircularProgress size={20} color="inherit"/> : null}
                                >
                                    Publish Active
                                </Button>
                            </>
                        )}
                    </Box>
                </DialogActions>
            </Dialog>

            <Dialog open={!!modalMode && modalMode === 'editMeta'} onClose={handleModalClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'medium' }}>
                        {selectedItem ? `Edit Metadata: ${selectedItem.title} (V${selectedItem.version})` : 'Edit Content'}
                    </Typography>
                    <IconButton aria-label="close" onClick={handleModalClose}><CloseIcon /></IconButton>
                </DialogTitle>
                {selectedItem && renderEditModalContent()}
            </Dialog>

            <Dialog open={isViewModalOpen} onClose={handleViewModalClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'medium' }}>
                        {selectedItemForView ? `Preview: ${selectedItemForView.title}` : 'Preview'}
                    </Typography>
                    <IconButton aria-label="close" onClick={handleViewModalClose}><CloseIcon /></IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    {selectedItemForView && (() => {
                        const { content_type, viewable_url, processed_content, description, source_url } = selectedItemForView;
                        const embedUrl = getEmbedUrl(viewable_url || source_url); 

                        if (embedUrl) {
                            return (
                                <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', maxWidth: '100%', bgcolor: '#000' }}>
                                    <iframe
                                        src={embedUrl} title="Video Preview" frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                                    />
                                </Box>
                            );
                        }
                        
                        const blocks = processed_content || [];
                        if (blocks.length > 0) {
                            return (
                                <>
                                    {description && <Typography sx={{ mb: 2, fontStyle: 'italic', color: 'text.secondary' }}>{description}</Typography>}
                                    {blocks.map((block, index) => {
                                        const questionDef = {
                                            id: block.id,
                                            question: block.question || block.prompt_text,
                                            type: block.type || block.block_type,
                                            options: block.options || {}
                                        };
                                        return <PatientQuestionRenderer key={block.id || index} questionDefinition={questionDef} mode="preview" />;
                                    })}
                                </>
                            );
                        }
                        
                        if (viewable_url) {
                            return (
                                <Box sx={{p: 3, textAlign: 'center'}}>
                                    <Typography gutterBottom>This content (like a PDF or local video) cannot be previewed here due to security policies.</Typography>
                                    <Button variant="contained" component="a" href={viewable_url} target="_blank" rel="noopener noreferrer" startIcon={<DownloadIcon />}>
                                        Open File in New Tab
                                    </Button>
                                </Box>
                            );
                        }

                        return (
                            <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                                This content item (Type: {content_type}) has no previewable content.
                            </Typography>
                        );
                    })()}
                </DialogContent>


                <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                    <Button onClick={handleViewModalClose}>Close</Button>
                </DialogActions>
            </Dialog>    
            {isProgramBuilderOpen && (
                <UnifiedProgramBuilder
                    open={isProgramBuilderOpen}
                    onClose={handleCloseProgramBuilder}
                    tenantId={tenantId}
                    initialData={programToEdit}
                />
            )}            
            <Dialog open={isTemplateEditorOpen} onClose={() => { setIsTemplateEditorOpen(false); setDuplicateBundleData(null); setIsBundleEditMode(false); }} maxWidth="lg" fullWidth> 
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Care Bundle Management
                    <IconButton aria-label="close" onClick={() => setIsTemplateEditorOpen(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                
                <DialogContent dividers>
                    <CareBundleBuilder
                        initialData={duplicateBundleData}
                        isEditMode={isBundleEditMode} // <-- ADD THIS LINE
                        tenantId={tenantId}
                        onUpdate={(payloadFromBuilder) => {
                            if (!payloadFromBuilder.name) {
                                alert("A Bundle Name is required to save.");
                                return;
                            }
                            const finalPayload = {
                                ...payloadFromBuilder,
                                is_active: true
                            };

                            saveTemplateMutation.mutate(finalPayload, {
                                onSuccess: () => {
                                    setIsTemplateEditorOpen(false);
                                    queryClient.invalidateQueries({ queryKey: ['careBundles'] });
                                    queryClient.invalidateQueries({ queryKey: ['careProtocols'] });
                                }
                            });
                        }}
                    />

                </DialogContent>
            </Dialog>
            
        </Container>
    );
}
export default ContentStudio;